import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { adminService, AutoTradingRecord } from '../services/adminService';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { AutoTradingActionFilter, AutoTradingStatusFilter, POSITION_SIDE } from '@/services/enums';

type OperationTypeFilter = AutoTradingActionFilter;
type StatusFilter = AutoTradingStatusFilter;

const AutoTradingRecords: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<AutoTradingRecord[]>([]);
  const [summary, setSummary] = useState<{
    total: number;
    success: number;
    failed: number;
    placeOrder: number;
    closePosition: number;
  } | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }>({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [selectedOperationType, setSelectedOperationType] = useState<OperationTypeFilter>('all');
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('all');
  const [symbolFilter, setSymbolFilter] = useState('');
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  // const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null); // Unused variable
  const hasLoadedRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingRef = useRef(false);

  const fetchRecords = async (page: number = pagination.page) => {
    // 防止重复调用
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);

      const operationType = selectedOperationType === 'all' ? undefined : selectedOperationType;
      const status = selectedStatus === 'all' ? undefined : selectedStatus;
      const symbol = symbolFilter.trim() || undefined;

      const response = await adminService.getAutoTradingRecords(
        page,
        pagination.pageSize,
        operationType,
        status,
        symbol,
      );

      setRecords(response.records);
      setPagination(response.pagination);
      setSummary(response.summary);
    } catch (error: any) {
      console.error('获取交易记录失败:', error);
      showToast(error.message || '获取交易记录失败', 'error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // 初始加载
  useEffect(() => {
    // 防止重复初始化
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 筛选条件变化时的防抖处理（不包括 symbolFilter，它需要按回车键查询）
  useEffect(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 如果已经初始化过，才执行防抖加载
    if (hasLoadedRef.current) {
      // 设置防抖定时器（500ms）
      debounceTimerRef.current = setTimeout(() => {
        fetchRecords(1);
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperationType, selectedStatus]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/20 text-green-300 border-green-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-300 border-red-500/50';
      default:
        return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success':
        return '成功';
      case 'failed':
        return '失败';
      default:
        return status;
    }
  };

  const getOperationTypeLabel = (operationType: string) => {
    switch (operationType) {
      case 'place_order':
        return '下单';
      case 'close_position':
        return '平仓';
      default:
        return operationType;
    }
  };

  const getDirectionLabel = (direction?: string | null) => {
    switch (direction) {
      case POSITION_SIDE.LONG:
        return '做多';
      case POSITION_SIDE.SHORT:
        return '做空';
      default:
        return '-';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // const handleRefresh = () => { // Unused variable
  //   fetchRecords(pagination.page);
  // };

  const handlePageChange = (newPage: number) => {
    if (pagination && (newPage < 1 || newPage > pagination.totalPages)) {
      return;
    }
    fetchRecords(newPage);
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="自动交易记录" onBack={() => setCurrentPage('personal-center')} />

        {/* 筛选栏 */}
        <div
          className="fixed left-0 right-0 z-40 bg-black/10 backdrop-blur-sm border-b border-white/10 px-4 py-2 space-y-2"
          style={{ top: '56px' }}
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchRecords(1);
                }
              }}
              placeholder="币种筛选"
              className="w-24 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <select
              value={selectedOperationType}
              onChange={(e) => {
                setSelectedOperationType(e.target.value as OperationTypeFilter);
              }}
              className="flex-1 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/40"
            >
              <option value="all" className="bg-gray-800 text-white">
                全部
              </option>
              <option value="place_order" className="bg-gray-800 text-white">
                下单
              </option>
              <option value="close_position" className="bg-gray-800 text-white">
                平仓
              </option>
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value as StatusFilter);
              }}
              className="flex-1 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/40"
            >
              <option value="all" className="bg-gray-800 text-white">
                全部
              </option>
              <option value="success" className="bg-gray-800 text-white">
                成功
              </option>
              <option value="failed" className="bg-gray-800 text-white">
                失败
              </option>
            </select>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="px-4 py-4" style={{ marginTop: '120px', paddingBottom: pagination ? '80px' : '20px' }}>
          {/* 统计摘要 */}
          {summary && (
            <div className="mb-6">
              <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-white/90 text-base font-semibold">交易统计</h2>
                  <span className="text-white/60 text-sm">总计: {summary.total}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">成功</div>
                    <div className="text-green-300 text-lg font-bold">{summary.success}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">失败</div>
                    <div className="text-red-300 text-lg font-bold">{summary.failed}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">下单</div>
                    <div className="text-white text-lg font-bold">{summary.placeOrder}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">平仓</div>
                    <div className="text-white text-lg font-bold">{summary.closePosition}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 加载状态 */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">暂无交易记录</div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {records.map((record) => (
                  <div key={record.id} className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90">
                    {/* 第一行：操作类型、交易对和状态 */}
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                        <span className="text-white font-semibold whitespace-nowrap flex-shrink-0">
                          {getOperationTypeLabel(record.operationType)}
                        </span>
                        <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 whitespace-nowrap overflow-hidden text-ellipsis block">
                          {record.symbol}
                        </span>
                        {record.direction && (
                          <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 whitespace-nowrap flex-shrink-0">
                            {getDirectionLabel(record.direction)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-1 rounded text-xs border ${getStatusColor(record.status)}`}>
                          {getStatusLabel(record.status)}
                        </span>
                      </div>
                    </div>

                    {/* 第二行：详细信息 */}
                    <div className="text-xs text-white/60 mb-2 space-y-1">
                      {record.triggerPrice && <div>触发价格: {parseFloat(record.triggerPrice).toFixed(8)}</div>}
                      {record.orderSize && <div>订单数量: {record.orderSize}</div>}
                      {record.positionSize && <div>持仓数量: {record.positionSize}</div>}
                      {record.takeProfitRatio && (
                        <div>止盈比例: {(parseFloat(record.takeProfitRatio) * 100).toFixed(2)}%</div>
                      )}
                      {record.stopLossRatio && (
                        <div>止损比例: {Math.abs(parseFloat(record.stopLossRatio) * 100).toFixed(2)}%</div>
                      )}
                      {record.okxOrdId && <div>订单ID: {record.okxOrdId}</div>}
                      {record.errorMessage && (
                        <div className="text-red-400 mt-1 break-words">错误: {record.errorMessage}</div>
                      )}
                    </div>

                    {/* 第三行：时间信息 */}
                    <div className="flex items-end justify-between text-xs text-white/60">
                      <div>
                        <div className="text-white/40 mb-1">创建时间</div>
                        <div>{formatDate(record.createdAt)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* 分页组件 */}
        {pagination && (
          <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-between p-4 bg-black/20 backdrop-blur-md border-t border-white/10">
            <div className="text-sm text-white/60 whitespace-nowrap">
              共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={loading || pagination.page <= 1}
                className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={loading || pagination.page >= pagination.totalPages}
                className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </GradientBackground>
  );
};

export default AutoTradingRecords;
